import React from 'react';
import type {
  SelectOption,
  TaskDefinitionRequest,
} from 'src/api/ProactApiClient';
import {TaskDefinitionStateEnum} from 'src/api/ProactApiClient';
import {ContentEditorSection} from 'src/app/content-pdf/ContentEditorSection';
import {excludeContentFields} from 'src/app/content-pdf/contentPdf';
import {DueDatePreviewForm} from 'src/app/forms/DueDatePreviewForm';
import {ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {taskFieldDefinitions} from 'src/app/tasks/taskHelpers';
import type {UseTaskFeatureFormMeta} from 'src/app/tasks/createTaskFeatureEditor';
import {
  createTaskFeatureEditor,
  useComplianceFormMeta,
} from 'src/app/tasks/createTaskFeatureEditor';
import {mapComplianceStandardIds} from 'src/app/tasks/taskFeatureTypes';
import {Box} from 'src/design/atoms/Box';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {Stack} from 'src/design/atoms/Stack';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';

export const getToolboxTalkFormMeta = (
  taskState: TaskDefinitionStateEnum,
  standards: SelectOption[]
): IFormFieldV2[] => [
  taskFieldDefinitions.projectId,
  taskFieldDefinitions.willAssignTo,
  taskFieldDefinitions.subject,
  taskFieldDefinitions.approvalFlowType,
  taskFieldDefinitions.dueInDays,
  taskState === TaskDefinitionStateEnum.Draft
    ? taskFieldDefinitions.previewDueDate
    : taskFieldDefinitions.dueDate,
  taskFieldDefinitions.optionalContent,
  taskFieldDefinitions.contentPdf,
  taskFieldDefinitions.assetId,
  taskFieldDefinitions.complianceStandards(standards),
];

export const mapToolboxTalkRequest = (
  values: IFormDataV2
): TaskDefinitionRequest => ({
  projectId: values.projectId,
  targetSourceEntityRequest: values.sourceEntities,
  subject: values.subject ?? '',
  approvalFlowType: values.approvalFlowType,
  dueInDays: values.dueInDays,
  content: values.content ?? '',
  contentPdfUrl: values.contentPdfUrl || undefined,
  assetId: values.assetId,
  targetComplianceStandardIds: mapComplianceStandardIds(values),
});

export const useToolboxTalkFormMeta: UseTaskFeatureFormMeta = (
  taskState,
  transformMeta
) => useComplianceFormMeta(getToolboxTalkFormMeta, taskState, transformMeta);

export const ToolboxTalkForm: React.FC<
  React.ComponentProps<typeof DueDatePreviewForm>
> = props => {
  const detailsMeta = excludeContentFields(props.form.formMeta);
  return (
    <DueDatePreviewForm {...props}>
      <Stack gap="md" mb="xl">
        <SectionCard title="Assignment details" data-testid="section-details">
          <Box p="md">
            <ProactFormGrid
              fieldNames={detailsMeta.map(field => field.name)}
              mb={0}
            />
          </Box>
        </SectionCard>
        <ContentEditorSection />
      </Stack>
    </DueDatePreviewForm>
  );
};

export const ToolboxTalkEditor = createTaskFeatureEditor(
  useToolboxTalkFormMeta,
  mapToolboxTalkRequest,
  ToolboxTalkForm
);
